import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  createUserRequest,
  deleteUserRequest,
  fetchUsersList,
  getCreatorPayloadFromToken,
  updateUserRequest,
  assignUserAttendanceRequest,
} from "../api/userBuilderApi";
import {
  GF_EMAIL_REGEX,
  gfIsISODateOnOrBeforeToday,
  gfTodayLocalISODate,
  gfValidateNameTextField,
  gfValidatePasswordField
} from "../../../global/forms/globalFormValidation";
import {
  resolveUserStatusId,
  filterUserBuilderRoles
} from "./userBuilderHelpers";
import { isProtectedUserBuilderAccount } from "../../../public/utils/roleAccess";
import { redirectToSessionTimeout } from "../../../public/api/httpClient";

const UserSectionContext = createContext(null);

const PAGE_SIZE = 10;

const USER_CODE_REGEX = /^GNWF-[A-Z]{3}-\d{6}$/;

function defaultUserDirectoryFilters() {
  return {
    user_code_search: "",
    column: "",
    value: ""
  };
}

function userRowLooksActive(statuses, userStatusId) {
  if (userStatusId == null || userStatusId === "") return true;
  const s = (statuses || []).find((x) => Number(x.id) === Number(userStatusId));
  if (!s) return true;
  const low = String(s.status_name || "").toLowerCase();
  if (low.includes("inact") || low.includes("disable")) return false;
  return true;
}

function emptyFieldErrors() {
  return {
    name: "",
    email: "",
    password: "",
    contact_number: "",
    date_of_joining: "",
    role_id: "",
    address: "",
    user_photo: "",
  };
}

function validateUserForm(form, isEdit) {
  const name = (form.name ?? "").trim();
  const email = (form.email ?? "").trim();
  const password = String(form.password ?? "").trim();
  const contact = (form.contact_number ?? "").replace(/\D/g, "");
  const doj = (form.date_of_joining ?? "").trim();
  const address = (form.address ?? "").trim();
  const roleId = form.role_id;

  const fieldErrors = emptyFieldErrors();

  if (!name) fieldErrors.name = "Name is required";
  else {
    const nameErr = gfValidateNameTextField(name);
    if (nameErr) fieldErrors.name = nameErr;
  }

  if (!email) fieldErrors.email = "Email is required";
  else if (!GF_EMAIL_REGEX.test(email)) fieldErrors.email = "Invalid email format";

  if (!isEdit) {
    if (!password) fieldErrors.password = "Password is required for new users";
    else {
      const pwErr = gfValidatePasswordField(password);
      if (pwErr) fieldErrors.password = pwErr;
    }
  } else if (password) {
    const pwErr = gfValidatePasswordField(password);
    if (pwErr) fieldErrors.password = pwErr;
  }

  const roleNum = roleId === "" || roleId == null ? NaN : Number(roleId);
  if (Number.isNaN(roleNum) || roleNum <= 0) {
    fieldErrors.role_id = "Select a role";
  }

  if (contact.length !== 10) {
    fieldErrors.contact_number = "Contact number must be exactly 10 digits";
  }

  if (!doj) {
    fieldErrors.date_of_joining = "Date of joining is required";
  } else if (!gfIsISODateOnOrBeforeToday(doj)) {
    fieldErrors.date_of_joining =
      "Choose a valid date on or before today";
  }

  if (!address) fieldErrors.address = "Address is required";

  const ok = !Object.values(fieldErrors).some(Boolean);
  const roleIdNum =
    !Number.isNaN(roleNum) && roleNum > 0 ? roleNum : null;

  return {
    ok,
    fieldErrors,
    name,
    email,
    password,
    contact,
    doj,
    address,
    roleIdNum
  };
}

const emptyForm = () => ({
  name: "",
  email: "",
  password: "",
  role_id: "",
  contact_number: "",
  date_of_joining: "",
  address: "",
  attendance_shift_id: "",
  attendance_location_id: "",
});

function toDateInputValue(v) {
  if (v == null || v === "") return "";
  const s = typeof v === "string" ? v : String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function buildListFilters(applied) {
  const out = {};
  const queryCode = String(applied.user_code_search || "").trim().toUpperCase();
  const col = applied.column;
  const val = String(applied.value || "").trim();

  if (col === "user_code" && val) {
    out.userCode = val.trim().toUpperCase();
  } else if (queryCode) {
    out.userCode = queryCode;
  }

  if (!col || !val) return out;

  if (col === "name") out.name = val;
  if (col === "email") out.email = val;
  if (col === "role") out.roleId = val;
  if (col === "status") out.userStatusId = val;
  if (col === "phone") {
    const digits = val.replace(/\D/g, "");
    if (digits.length === 10) out.contact = digits;
  }
  if (col === "doj") {
    const s = val.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) out.dateOfJoining = s;
  }
  if (col === "shift") out.attendanceShiftId = val;
  if (col === "location") out.attendanceLocationId = val;
  return out;
}

export function UserSectionProvider({
  children,
  roles,
  statuses,
  attendanceShifts,
  attendanceLocations,
  onUsersChanged,
  onNotify,
  onEngageSection
}) {
  const [editingUserId, setEditingUserId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [userPendingRemoval, setUserPendingRemoval] = useState(null);
  const [removingUser, setRemovingUser] = useState(false);
  const [userPendingAssignment, setUserPendingAssignment] = useState(null);
  const [assigningUser, setAssigningUser] = useState(false);

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(() =>
    defaultUserDirectoryFilters()
  );
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(() =>
    defaultUserDirectoryFilters()
  );
  const [directoryError, setDirectoryError] = useState("");
  const [fieldErrors, setFieldErrors] = useState(emptyFieldErrors);
  const [submitError, setSubmitError] = useState("");
  const [saveAsActive, setSaveAsActive] = useState(true);
  const [photoFile, setPhotoFile] = useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const formAlertRef = useRef(null);

  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
  const showFormAlert = !!(submitError || hasFieldErrors);

  useEffect(() => {
    if (!showFormAlert) return;
    formAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [showFormAlert, submitError, hasFieldErrors]);

  const loadPage = useCallback(
    async (p) => {
      setListLoading(true);
      setDirectoryError("");
      const code = String(appliedDirectoryFilters.user_code_search || "")
        .trim()
        .toUpperCase();
      if (code && !USER_CODE_REGEX.test(code)) {
        setDirectoryError("Search code must match GNWF-XXX-######");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
        setListLoading(false);
        return;
      }
      try {
        const apiFilters = buildListFilters(appliedDirectoryFilters);
        const r = await fetchUsersList(p, PAGE_SIZE, apiFilters);
        setRows(r.rows);
        setTotal(r.total);
        setTotalPages(r.totalPages);
        setPage(r.page);
      } catch (e) {
        setRows([]);
        const msg = e.message || "Could not load users.";
        setDirectoryError(msg);
        onNotify?.(msg, { variant: "error" });
      } finally {
        setListLoading(false);
      }
    },
    [appliedDirectoryFilters, onNotify]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedDirectoryFilters({ ...draftDirectoryFilters });
    }, 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    setPage(1);
  }, [appliedDirectoryFilters]);

  useEffect(() => {
    loadPage(page);
  }, [page, loadPage]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) =>
      prev[name] ? { ...prev, [name]: "" } : prev
    );
    setSubmitError("");
  };

  const handlePhotoChange = (file, photoError = "") => {
    setPhotoFile(file);
    setFieldErrors((prev) => ({ ...prev, user_photo: photoError }));
    setSubmitError("");
  };

  const resetForm = () => {
    setEditingUserId(null);
    setForm(emptyForm());
    setFieldErrors(emptyFieldErrors());
    setSubmitError("");
    setSaveAsActive(true);
    setPhotoFile(null);
    setExistingPhotoUrl("");
  };

  const startEdit = (u) => {
    if (isProtectedUserBuilderAccount(u)) {
      onNotify?.(
        u?.role_slug === "donor" || u?.role_slug === "students"
          ? "Donor and student accounts cannot be edited from User builder."
          : "The system administrator account cannot be edited."
      );
      return;
    }
    setEditingUserId(u.id);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role_id: u.role_id != null ? String(u.role_id) : "",
      contact_number: String(u.contact_number || "").replace(/\D/g, "").slice(0, 10),
      date_of_joining: toDateInputValue(u.date_of_joining),
      address: u.address || "",
      attendance_shift_id:
        u.attendance_shift_id != null ? String(u.attendance_shift_id) : "",
      attendance_location_id:
        u.attendance_location_id != null ? String(u.attendance_location_id) : "",
    });
    setFieldErrors(emptyFieldErrors());
    setSubmitError("");
    setSaveAsActive(userRowLooksActive(statuses, u.user_status_id));
    setPhotoFile(null);
    setExistingPhotoUrl(u.photo || "");
    onEngageSection?.();
    window.setTimeout(() => {
      document.getElementById("user-name")?.focus({ preventScroll: true });
    }, 80);
  };

  const submit = async () => {
    setSubmitError("");
    setFieldErrors(emptyFieldErrors());

    const creator = getCreatorPayloadFromToken();
    if (!creator?.created_by) {
      redirectToSessionTimeout();
      return;
    }

    const mode = saveAsActive ? "active" : "inactive";
    const user_status_id = resolveUserStatusId(statuses, mode);
    if (user_status_id == null) {
      setSubmitError("Could not resolve user status from masters.");
      return;
    }

    const wasEdit = editingUserId != null;
    if (wasEdit) {
      const editingRow = rows.find((r) => Number(r.id) === Number(editingUserId));
      if (isProtectedUserBuilderAccount(editingRow || { id: editingUserId })) {
        setSubmitError(
          editingRow?.role_slug === "donor" || editingRow?.role_slug === "students"
            ? "Donor and student accounts cannot be edited from User builder."
            : "The system administrator account cannot be edited."
        );
        return;
      }
    }
    const check = validateUserForm(form, wasEdit);
    if (!wasEdit) {
      if (!(photoFile instanceof File)) {
        check.fieldErrors.user_photo = "Profile photo is required";
        check.ok = false;
      }
    } else if (!(photoFile instanceof File) && !existingPhotoUrl) {
      check.fieldErrors.user_photo = "Profile photo is required";
      check.ok = false;
    }
    setFieldErrors(check.fieldErrors);
    if (!check.ok) {
      return;
    }
    if (check.fieldErrors.user_photo) {
      return;
    }

    setBusy(true);
    try {
      const attendancePayload = {
        attendance_shift_id: form.attendance_shift_id
          ? Number(form.attendance_shift_id)
          : null,
        attendance_location_id: form.attendance_location_id
          ? Number(form.attendance_location_id)
          : null,
      };

      if (wasEdit) {
        const payload = {
          user_status_id,
          role_id: check.roleIdNum,
          name: check.name,
          email: check.email,
          contact_number: check.contact,
          date_of_joining: check.doj,
          address: check.address,
          ...attendancePayload,
        };
        if (check.password) {
          payload.password = check.password;
        }
        await updateUserRequest(editingUserId, payload, photoFile);
        onNotify?.("User updated.");
      } else {
        const created = await createUserRequest({
          user_status_id,
          role_id: check.roleIdNum,
          name: check.name,
          email: check.email,
          password: check.password,
          contact_number: check.contact,
          date_of_joining: check.doj,
          address: check.address,
          created_by: creator.created_by,
          created_by_type: creator.created_by_type,
          ...attendancePayload,
        }, photoFile);
        onNotify?.(created?.message || "User created.");
      }
      resetForm();
      await onUsersChanged?.();
      if (wasEdit) {
        await loadPage(page);
      } else if (page === 1) {
        await loadPage(1);
      } else {
        setPage(1);
      }
    } catch (e) {
      const details = e?.details;
      if (details?.user_photo) {
        setFieldErrors({ ...emptyFieldErrors(), user_photo: details.user_photo });
      } else {
        setFieldErrors(emptyFieldErrors());
      }
      setSubmitError(e.message || "Could not save user.");
    } finally {
      setBusy(false);
    }
  };

  const requestUserRemoval = (u) => {
    if (isProtectedUserBuilderAccount(u)) {
      onNotify?.(
        u?.role_slug === "donor" || u?.role_slug === "students"
          ? "Donor and student accounts cannot be removed from User builder."
          : "The system administrator account cannot be removed."
      );
      return;
    }
    setUserPendingRemoval(u);
  };

  const cancelUserRemoval = () => {
    if (removingUser) return;
    setUserPendingRemoval(null);
  };

  const confirmUserRemoval = async () => {
    const u = userPendingRemoval;
    if (!u || removingUser) return;
    onNotify?.("");
    setRemovingUser(true);
    setBusy(true);
    try {
      await deleteUserRequest(u.id);
      if (editingUserId === u.id) resetForm();
      onNotify?.("User removed from the directory.");
      await onUsersChanged?.();
      await loadPage(page);
      setUserPendingRemoval(null);
    } catch (e) {
      onNotify?.(e.message || "Could not remove user.", { variant: "error" });
    } finally {
      setBusy(false);
      setRemovingUser(false);
    }
  };

  const requestAttendanceAssignment = (u) => {
    if (isProtectedUserBuilderAccount(u)) {
      onNotify?.("Protected accounts cannot be assigned attendance settings.");
      return;
    }
    setUserPendingAssignment(u);
  };

  const cancelAttendanceAssignment = () => {
    if (assigningUser) return;
    setUserPendingAssignment(null);
  };

  const confirmAttendanceAssignment = async (body) => {
    const u = userPendingAssignment;
    if (!u || assigningUser) return;
    setAssigningUser(true);
    setBusy(true);
    try {
      await assignUserAttendanceRequest(u.id, body);
      onNotify?.("Attendance shift and location saved.");
      await loadPage(page);
      setUserPendingAssignment(null);
    } catch (e) {
      onNotify?.(e.message || "Could not save assignment.", { variant: "error" });
    } finally {
      setBusy(false);
      setAssigningUser(false);
    }
  };

  const isEdit = editingUserId != null;
  const maxDateOfJoining = gfTodayLocalISODate();
  const visibleRoles = filterUserBuilderRoles(roles);

  const clearDirectoryFilters = useCallback(() => {
    const empty = defaultUserDirectoryFilters();
    setDraftDirectoryFilters(empty);
    setAppliedDirectoryFilters(empty);
    setDirectoryError("");
    onNotify?.("");
  }, [onNotify]);

  const value = {
    roles,
    statuses,
    attendanceShifts: attendanceShifts || [],
    attendanceLocations: attendanceLocations || [],
    form,
    setField,
    submit,
    resetForm,
    isEdit,
    busy,
    showFormAlert,
    formAlertRef,
    fieldErrors,
    submitError,
    visibleRoles,
    maxDateOfJoining,
    saveAsActive,
    setSaveAsActive,
    photoFile,
    setPhotoFile: handlePhotoChange,
    existingPhotoUrl,
    draftDirectoryFilters,
    setDraftDirectoryFilters,
    clearDirectoryFilters,
    directoryError,
    listLoading,
    page,
    totalPages,
    total,
    setPage,
    rows,
    startEdit,
    userPendingRemoval,
    removingUser,
    requestUserRemoval,
    cancelUserRemoval,
    confirmUserRemoval,
    userPendingAssignment,
    assigningUser,
    requestAttendanceAssignment,
    cancelAttendanceAssignment,
    confirmAttendanceAssignment,
    pageSize: PAGE_SIZE
  };

  return (
    <UserSectionContext.Provider value={value}>
      {children}
    </UserSectionContext.Provider>
  );
}

export function useUserSectionContext() {
  const ctx = useContext(UserSectionContext);
  if (!ctx) {
    throw new Error("useUserSectionContext must be used within UserSectionProvider");
  }
  return ctx;
}
